import { useId } from "react";

import {
  diffLabTurns,
  labTurnByNumber,
  type LabRunData,
  type LabTurn,
  type LabTurnDiff,
} from "@/lib/labModel";
import type { LabRunModel } from "@/lib/labRunModel";
import {
  LAB_TURN_TABS,
  LAB_TURN_TAB_HELP,
  LAB_TURN_TAB_LABELS,
  type LabTurnTabId,
} from "@/lib/labUrl";
import { Badge } from "@/ui/Badge";
import { EmptyState } from "@/ui/states";

import { LabAssemblyTab } from "./turn/LabAssemblyTab";
import { LabClassificationStateTab } from "./turn/LabClassificationStateTab";
import { LabClassificationTab } from "./turn/LabClassificationTab";
import { LabCriteriaTab } from "./turn/LabCriteriaTab";
import { LabSafetyTab } from "./turn/LabSafetyTab";
import { LabTurnServicesTab } from "./turn/LabServicesTab";
import { LabTabStrip } from "./turn/LabTabStrip";
import { LabTurnSummary, SideScope } from "./turn/LabTurnSummary";

const TURN_TABS = LAB_TURN_TABS.map((id) => ({
  id,
  label: LAB_TURN_TAB_LABELS[id],
  help: LAB_TURN_TAB_HELP[id],
}));
const STRIP_ID = "lab-turn";

/**
 * The Lab's right pane, "This turn" scope: everything the run recorded at ONE
 * turn, grouped by kind. In compare mode the same sections are shown for
 * both runs side by side and every axis on which the two differ says so in
 * words.
 *
 * Reads nothing about the run itself (`LabRunData` arrives by props). Its body
 * reads are the event-detail read, for a classification's result and rationale
 * or a prompt call's bodies, only after the clinician presses the control that
 * asks for it, and the Classification state tab's own reads of the run's documents
 * and the main-concern result, made automatically and one request at a time.
 */
export function LabTurnInspector({
  projectId,
  primary,
  compare,
  selectedTurn,
  itab,
  onItabChange,
  model,
  compareModel,
  onSelectTurn,
}: {
  projectId: string;
  primary: LabRunData | null;
  compare: LabRunData | null;
  selectedTurn: number | null;
  /** The selected turn tab. Controlled: the URL belongs to the caller. */
  itab: LabTurnTabId;
  onItabChange: (id: LabTurnTabId) => void;
  model: LabRunModel | null;
  compareModel: LabRunModel | null;
  onSelectTurn: (turn: number) => void;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="sr-only">
        Turn inspector
      </h2>
      {primary === null ? (
        <EmptyState
          title="No active conversation"
          description="Choose the settings above and press New conversation, or open one from History."
        />
      ) : (
        <>
          {selectedTurn === null ? (
            <p className="text-sm text-muted-foreground">
              Select a turn in the conversation to see everything that happened
              at it.
            </p>
          ) : (
            <TurnView
              projectId={projectId}
              primary={primary}
              compare={compare}
              turnNumber={selectedTurn}
              itab={itab}
              onItabChange={onItabChange}
              model={model}
              compareModel={compareModel}
              onSelectTurn={onSelectTurn}
            />
          )}
        </>
      )}
    </section>
  );
}

function TurnView({
  projectId,
  primary,
  compare,
  turnNumber,
  itab,
  onItabChange,
  model,
  compareModel,
  onSelectTurn,
}: {
  projectId: string;
  primary: LabRunData;
  compare: LabRunData | null;
  turnNumber: number;
  itab: LabTurnTabId;
  onItabChange: (id: LabTurnTabId) => void;
  model: LabRunModel | null;
  compareModel: LabRunModel | null;
  onSelectTurn: (turn: number) => void;
}) {
  const a = labTurnByNumber(primary.lab.turns, turnNumber);
  const b =
    compare === null ? null : labTurnByNumber(compare.lab.turns, turnNumber);
  const diff = a !== null && b !== null ? diffLabTurns(a, b) : null;

  if (compare === null) {
    return (
      <div className="space-y-3">
        <TurnHeading turnNumber={turnNumber} chip={positionChip(primary, a)} />
        {a === null ? (
          <MissingTurn run={primary} turnNumber={turnNumber} />
        ) : (
          <>
            <LabTabStrip
              idPrefix={STRIP_ID}
              ariaLabel="Turn details"
              tabs={TURN_TABS}
              value={itab}
              onChange={onItabChange}
              panelId={PANEL_ID}
            />
            <TurnPanel itab={itab}>
              <TurnTab
                itab={itab}
                projectId={projectId}
                run={primary}
                turn={a}
                model={model}
                onSelectTurn={onSelectTurn}
              />
            </TurnPanel>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <TurnHeading turnNumber={turnNumber} chip={null} />
      <LabTabStrip
        idPrefix={STRIP_ID}
        ariaLabel="Turn details"
        tabs={TURN_TABS}
        value={itab}
        onChange={onItabChange}
        panelId={PANEL_ID}
      />
      <TurnPanel itab={itab}>
        <div className="grid grid-cols-2 gap-3 max-[899px]:grid-cols-1">
          {(
            [
              ["Run A", primary, a],
              ["Run B", compare, b],
            ] as const
          ).map(([label, run, turn]) => (
            <div
              key={label}
              role="group"
              aria-label={label}
              className="min-w-0 space-y-3"
            >
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
                {positionChip(run, turn) !== null ? (
                  <Badge tone="outline">{positionChip(run, turn)}</Badge>
                ) : null}
              </p>
              {turn === null ? (
                <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  No turn {turnNumber} in this run
                </p>
              ) : (
                <TurnTab
                  itab={itab}
                  projectId={projectId}
                  run={run}
                  turn={turn}
                  model={run === primary ? model : compareModel}
                  onSelectTurn={onSelectTurn}
                  diff={diff}
                  sideLabel={label}
                />
              )}
            </div>
          ))}
        </div>
      </TurnPanel>
    </div>
  );
}

const PANEL_ID = `${STRIP_ID}-panel`;

/** The one tabpanel every turn tab controls, wired to the strip's selected tab. */
function TurnPanel({
  itab,
  children,
}: {
  itab: LabTurnTabId;
  children: React.ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={PANEL_ID}
      aria-labelledby={`${STRIP_ID}-${itab}`}
      tabIndex={0}
      className="min-w-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      {children}
    </div>
  );
}

/** The selected tab's content for one run's turn. */
function TurnTab({
  itab,
  projectId,
  run,
  turn,
  model,
  onSelectTurn,
  diff = null,
  sideLabel = null,
}: {
  itab: LabTurnTabId;
  projectId: string;
  run: LabRunData;
  turn: LabTurn;
  model: LabRunModel | null;
  onSelectTurn: (turn: number) => void;
  diff?: LabTurnDiff | null;
  sideLabel?: string | null;
}) {
  if (itab === "summary") {
    return (
      <LabTurnSummary
        projectId={projectId}
        runRef={run.previewRunRef}
        turn={turn}
        sim={run.sim}
        diff={diff}
        sideLabel={sideLabel}
      />
    );
  }
  if (itab === "state") {
    return (
      <SideScope sideLabel={sideLabel}>
        <LabClassificationStateTab
          projectId={projectId}
          run={run}
          throughTurn={turn.turnNumber}
        />
      </SideScope>
    );
  }
  if (itab === "services") {
    return (
      <SideScope sideLabel={sideLabel}>
        <LabTurnServicesTab projectId={projectId} run={run} turn={turn} />
      </SideScope>
    );
  }
  if (model === null) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        The run&apos;s details are still loading.
      </p>
    );
  }
  const props = {
    projectId,
    previewRunRef: run.previewRunRef,
    turn,
    model,
    onSelectTurn,
    diff,
  };
  return (
    <SideScope sideLabel={sideLabel}>
      {itab === "classification" ? (
        <LabClassificationTab {...props} />
      ) : itab === "criteria" ? (
        <LabCriteriaTab {...props} />
      ) : itab === "prompts" ? (
        <LabAssemblyTab {...props} />
      ) : (
        <LabSafetyTab {...props} />
      )}
    </SideScope>
  );
}

function TurnHeading({
  turnNumber,
  chip,
}: {
  turnNumber: number;
  chip: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h3 tabIndex={-1} className="text-base font-semibold">
        Turn {turnNumber}
      </h3>
      {chip !== null ? <Badge tone="outline">{chip}</Badge> : null}
    </div>
  );
}

function positionChip(run: LabRunData, turn: LabTurn | null): string | null {
  if (turn === null) {
    return null;
  }
  const index = run.lab.turns.findIndex(
    (t) => t.turnNumber === turn.turnNumber,
  );
  return index < 0 ? null : `${index + 1} of ${run.lab.turns.length} recorded`;
}

function MissingTurn({
  run,
  turnNumber,
}: {
  run: LabRunData;
  turnNumber: number;
}) {
  return (
    <p role="status" className="text-sm text-muted-foreground">
      This run has no turn {turnNumber}
      {run.live ? " yet" : ""}.
    </p>
  );
}
